import { UtColumnMap, UtKey, UtType } from './types';

export const UT_TYPES: UtType[] = [
  { key: 'CDA', label: 'CDA [Nl/min]' },
  { key: 'N2', label: 'N2 AVG [Nl/min]' },
  { key: 'IW', label: 'IW [ℓ/min]' },
  { key: 'PCW', label: 'PCW [ℓ/min]' },
  { key: 'Steam', label: 'Steam [t/hr]' },
  { key: 'NG', label: 'NG [Nm³/hr]' },
  { key: 'Return', label: 'Return air [CMH]' },
  { key: 'Exhaust', label: 'Exhaust air [CMH]' },
  { key: 'Supply', label: 'Supply air [CMH]' },
];

/**
 * UT 항목 → DB 코드(UT_MECH_CTGR_CD) 매핑.
 *
 * ⚠ 화면 내부 키(UtKey)와 DB 코드가 4개 항목에서 다르다. HTML 스펙 주석(⑧)의
 * ANLS_OPIN_TP_CD IN ('CDA','N2','IW','PCW','ST','NG','RA','EA','SA') 기준이며,
 * 현재 machineRowsToRptDetail()은 UtKey를 그대로 UTM_TP_CD에 넣고 있어 어느 쪽이
 * 맞는지 BE 확인이 필요하다(ref/BE-REQUEST-rpt-sum.md §4).
 * 조회 시에는 양쪽 표기를 모두 받아들이도록 findUtType()을 쓴다.
 */
export const UT_DB_CODE: Record<UtKey, string> = {
  CDA: 'CDA',
  N2: 'N2',
  IW: 'IW',
  PCW: 'PCW',
  Steam: 'ST',
  NG: 'NG',
  Return: 'RA',
  Exhaust: 'EA',
  Supply: 'SA',
};

/** 화면 키('Steam')든 DB 코드('ST')든 받아서 UT_TYPES 항목을 찾는다 */
export const findUtType = (code: string): UtType | undefined => {
  const c = (code || '').toUpperCase();
  return UT_TYPES.find((t) => t.key.toUpperCase() === c || UT_DB_CODE[t.key].toUpperCase() === c);
};

/** UT_TYPES.label에서 단위만 뽑는다 — 'CDA [Nl/min]' → 'Nl/min' */
export const utUnitOf = (code: string): string =>
  findUtType(code)?.label.match(/\[(.+)\]/)?.[1] ?? '';

export const UT_ALIASES: Record<UtKey, string[]> = {
  CDA: ['cda', '압축공기', '공압'],
  N2: ['n2', '질소'],
  IW: ['iw', '공업용수', '인더스트리얼워터'],
  PCW: ['pcw', '냉각수'],
  Steam: ['steam', '스팀', '증기'],
  NG: ['ng', '천연가스'],
  Return: ['return', '리턴에어', '리턴 에어', '회수공기'],
  Exhaust: ['exhaust', '배기', '배기에어', '배기 에어'],
  Supply: ['supply', '급기', '급기에어', '급기 에어'],
};

/** UT 항목 → TB_EELMB_UTM_D 컬럼 매핑 (usage=총사용량 Sum, unit=단위사용량, qty=Point/연결개수) */
export const UT_COLUMN_MAP: Record<UtKey, UtColumnMap> = {
  CDA: { usage: 'CDA_CSP_SUM_CAPA', unit: 'CDA_CSP_CAPA', qty: 'CDA_PNT_CNT' },
  N2: { usage: 'NITR_CSP_SUM_CAPA', unit: 'NITR_CSP_CAPA', qty: 'NITR_PNT_CNT' },
  IW: { usage: 'IW_CSP_SUM_CAPA', unit: 'IW_CSP_CAPA', qty: 'IW_PNT_CNT' },
  PCW: { usage: 'COOL_CSP_SUM_CAPA', unit: 'COOL_CSP_CAPA', qty: 'COOL_PNT_CNT' },
  Steam: { usage: 'STEM_CSP_SUM_CAPA', unit: 'STEM_CSP_CAPA', qty: 'STEM_PNT_CNT' },
  NG: { usage: 'LNG_CSP_SUM_CAPA', unit: 'LNG_CSP_CAPA', qty: 'LNG_PNT_CNT' },
  Return: { usage: 'INSD_EXHA_CSP_SUM_CAPA', unit: 'INSD_EXHA_CSP_CAPA', qty: 'INSD_EXHA_PNT_CNT' },
  Exhaust: {
    usage: 'OTSD_EXHA_CSP_SUM_CAPA',
    unit: 'OTSD_EXHA_CSP_CAPA',
    qty: 'OTSD_EXHA_PNT_CNT',
  },
  Supply: { usage: 'SUAR_CSP_SUM_CAPA', unit: 'SUAR_CSP_CAPA', qty: 'SUAR_CNCT_CNT' },
};

/** 동시 비교 가능한 UT Matrix 최대 개수 (1.기준 / 2.비교대상 / 3.참조) */
export const MAX_COMPARE = 3;

/** 리포트 생성 시 반드시 지정해야 하는 대상 수 (기준 + 비교대상) */
export const MIN_COMPARE = 2;

/**
 * 비교 대상 구분 — TB_EELMB_UTM_RPT_UTM_M.MST_TGT_TP_CD (공통코드 MST/TGT/REF)
 * 배열 순서가 곧 SORT_ORD(1,2,3)다. 참조(3)는 선택이며 리포트 상세에는 저장하지 않는다.
 */
export const MST_TGT_TYPES = [
  { cd: 'MST', nm: '기준' },
  { cd: 'TGT', nm: '비교대상' },
  { cd: 'REF', nm: '참조' },
];
